import React, { useState, useRef, useMemo, useEffect } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { process } from "@progress/kendo-data-query";
import {
  Search,
  LayoutGrid,
  List,
  Download,
  Upload,
  Calendar,
  Receipt,
  Building2,
  X,
} from "lucide-react";
import { eopApi } from "../../api/eopApi";
import { isApiConfigured } from "../../api/axiosClient";
import { LoadingSpinner, ErrorCard } from "../../components/LoadingSpinner";
import { ColumnMenuClaim } from "../../components/ColumnMenuClaim";
import { ColumnMenu } from "../../components/ColumnMenu";
import { CardsPagination } from "../../components/CardsPagination";
import { useAuth } from "../../auth/AuthContext";
import { userApi } from "../../api/userApi";
import { useToast } from "../../components/Toast";
import { CsvFileDownload } from "../../components/CsvFileDownload";
import { PdfFileDownload } from "../../components/PdfFileDownload";

const mockEOPData = [
  {
    id: 1,
    eopNumber: "EOP-2026-001847",
    checkNumber: "CHK-90412",
    provider: "Austin Primary Care Associates",
    paymentDate: "2026-03-15",
    amount: 1245.0,
    status: "Paid",
  },
  {
    id: 2,
    eopNumber: "EOP-2026-002103",
    checkNumber: "CHK-90578",
    provider: "Austin Primary Care Associates",
    paymentDate: "2026-03-28",
    amount: 875.5,
    status: "Paid",
  },
  {
    id: 3,
    eopNumber: "EOP-2026-002390",
    checkNumber: "CHK-90634",
    provider: "Austin Primary Care Associates",
    paymentDate: "2026-04-05",
    amount: 2310.0,
    status: "Pending",
  },
  {
    id: 4,
    eopNumber: "EOP-2026-002512",
    checkNumber: "CHK-90701",
    provider: "Austin Primary Care Associates",
    paymentDate: "2026-04-08",
    amount: 560.75,
    status: "Paid",
  },
];

const EOPSearchPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  // const [billingProvider, setBillingProvider] = useState(user.taxId?.trim()??'');
  const [billingProvider, setBillingProvider] = useState("");
  const [checkNumber, setCheckNumber] = useState("");
  const [groupId, setGroupId] = useState("");
  const [serviceStart, setServiceStart] = useState("2010-01-01");
  const today = new Date().toISOString().split("T")[0];
  const [serviceEnd, setServiceEnd] = useState(today);
  const [results, setResults] = useState([]);
  const [eraRetrivalMethod, setERARetrivalMethod] = useState("");
  const [isItTextSearch, setIsItTextSearch] = useState(false);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [IsExportingToExcel, setIsExportingToExcel] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [eopRequesting, setEopRequesting] = useState(false);
  const [requestingId, setRequestingId] = useState(null);
  const [eopCsvDownloadRequestingId, setEopCsvDownloadRequestingId] =
    useState(null);
  const [eopPdfDownloadRequestingId, setEopPdfDownloadRequestingId] =
    useState(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const [hiddenRequestButtonsTable, setHiddenRequestButtonsTable] = useState(
    [],
  );

  // 'cards' | 'table' — matches the User Management view toggle
  const [viewMode, setViewMode] = useState("cards");
  // Free-text search across all visible columns — works in both views.
  const [searchText, setSearchText] = useState("");
  const searchTimeoutRef = useRef(null);
  // Externalized Kendo Grid state so paging/sorting stays in sync between
  // Cards and Table.
  const [dataState, setDataState] = useState({
    skip: 0,
    take: 10,
    sort: [],
    filter: null,
  });
  const excelExportRef = useRef(null);
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState([]);

  const [userTaxIds, setUserTaxIds] = useState([]);

  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(255,255,255,0.7)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    gridOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(255,255,255,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
  };

  useEffect(() => {
    setAccessToken(localStorage.getItem("sendero_token"));

    fetchGroups();
  }, []);

  useEffect(() => {
    if (user?.userId) {
      getUserTaxIds(user?.userId);
    }
  }, [user?.userId]);

  const getUserTaxIds = async (userId) => {
    if (isApiConfigured()) {
      setPageLoading(true);

      try {
        var res = await userApi.getUsersTaxIds({
          userId: userId,
        });

        if (res.success) {
          setUserTaxIds(res?.data?.TaxIds);
          console.log("UserTaxIds get successfully");
        } else {
          setUserTaxIds([]);
          console.log(res.message);
        }
        setPageLoading(false);
      } catch (err) {
        console.log("Error at getUserTaxIds : " + err);
        setPageLoading(false);
      } finally {
      }
    }
  };

  const fetchGroups = async () => {
    const res = await userApi.groups();

    if (res.success) {
      setGroups(res.data.Groups);
    }
  };

  const parseDate = (str) => {
    if (!str) return null;

    const [month, day, year] = str.split(/[-/]/);
    return new Date(year, month - 1, day);
  };

  const handleSearch = async (
    customState = dataState,
    customSearch = searchText,
  ) => {
    setError(null);
    setSearched(true);
    setIsItTextSearch(false);
    setSearchText("");
    setDataState({ skip: 0, take: 10, sort: [], filter: null });

    if (isApiConfigured()) {
      const sort = customState.sort?.[0];
      setLoading(true);

      try {
        if (!billingProvider?.trim()) {
          toast.error("Billing Provider Id is required", {
            title: "Search EOP",
          });
          return;
        }

        const response = await eopApi.searchEOP({
          billingProvider,
          checkNumber,
          serviceStart,
          serviceEnd,
          groupId,
          searchText: "",
          sortColumnName: "",
          sortColumnDirection: "",
          startIndex: customState.skip || 0,
          pageSize: customState.take || 10,
        });

        const formattedData = (response.data.Eops || []).map((item) => ({
          ...item,
          Check_Date: parseDate(item?.Check_Date?.split(" ")[0]),
          //Check_Status: item?.Check_Status?.toLowerCase() === "p" ? 'Paid' : 'Pending',
          Check_Status: item?.Check_Status,
          //Paid_Amount: Number(item.Paid_Amount).toFixed(2)
          Paid_Amount: parseFloat(item?.Paid_Amount || 0),
        }));

        setResults(formattedData);
        setERARetrivalMethod(response?.data?.ERARetrivalMethod);
        setTotalRecords(response?.data?.Eops[0]?.TotalRecord || 0);
        setLoading(false);
      } catch (err) {
        setTotalRecords(0);
        console.log("HANDLESEARCH ERROR : " + err);
        setError(
          err.response?.data?.message ||
            "Failed to search EOP records. Please try again.",
        );
        setResults([]);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    } else {
      toast.error("API not configured.", { title: "Search EOP" });
    }
  };
  const formatSearchText = (text) => {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;

    if (!regex.test(text)) {
      return text;
    }

    const [month, day, year] = text.split("/");
    return `${year}-${month}-${day}`;
  };
  const exportToExcel = async (
    customState = dataState,
    customSearch = searchText,
  ) => {
    if (isApiConfigured()) {
      const sort = customState.sort?.[0];
      setIsExportingToExcel(true);

      try {
        //console.log("------------------------------");
        //console.log("billingProvider : " + billingProvider);
        //console.log("checkNumber : " + checkNumber);
        //console.log("serviceStart : " + serviceStart);
        //console.log("serviceEnd : " + serviceEnd);
        //console.log("groupId : " + groupId);
        //console.log("searchText : " + searchText);
        //console.log("sortColumnName : " + sort?.field);

        //console.log("sortColumnDirection : " + sort?.dir);
        //console.log("startIndex : " + customState.skip);
        //console.log("pageSize : " + customState.take);
        //console.log("------------------------------");

        const response = await eopApi.searchEOP({
          billingProvider,
          checkNumber,
          serviceStart,
          serviceEnd,
          groupId,
          searchText: formatSearchText(searchText || ""),
          sortColumnName: sort?.field || "",
          sortColumnDirection: sort?.dir || "",
          startIndex: 0,
          pageSize: totalRecords,
        });

        const formattedData = (response.data.Eops || []).map((item) => ({
          ...item,
          Check_Date: parseDate(item?.Check_Date?.split(" ")[0]),
          //Check_Status: item?.Check_Status?.toLowerCase() === "p" ? 'Paid' : 'Pending',
          Check_Status: item?.Check_Status,
          //Paid_Amount: Number(item.Paid_Amount).toFixed(2)
          Paid_Amount: parseFloat(item?.Paid_Amount || 0),
        }));
        setIsExportingToExcel(false);
        return formattedData;
      } catch (err) {
        setIsExportingToExcel(false);
        console.log("exportToExcel ERROR : " + err);
      } finally {
        setIsExportingToExcel(false);
      }
    } else {
      toast.error("API not configured.", { title: "Search EOP" });
    }
  };
  const handleSearch2 = async (
    customState = dataState,
    customSearch = searchText,
  ) => {
    setError(null);
    setSearched(true);
    setLoading(false);

    setIsItTextSearch(true);

    if (isApiConfigured()) {
      const sort = customState.sort?.[0];
      setGridLoading(true);

      try {
        if (!billingProvider?.trim()) {
          toast.error("Billing Provider Id is required", {
            title: "Search EOP",
          });
          return;
        }

        //console.log("------------------------------");
        //console.log("billingProvider : " + billingProvider);
        //console.log("checkNumber : " + checkNumber);
        //console.log("serviceStart : " + serviceStart);
        //console.log("serviceEnd : " + serviceEnd);
        //console.log("groupId : " + groupId);
        //console.log("searchText : " + searchText);
        //console.log("sortColumnName : " + sort?.field);

        //console.log("sortColumnDirection : " + sort?.dir);
        //console.log("startIndex : " + customState.skip);
        //console.log("pageSize : " + customState.take);
        //console.log("------------------------------");

        const response = await eopApi.searchEOP({
          billingProvider,
          checkNumber,
          serviceStart,
          serviceEnd,
          groupId,
          searchText: formatSearchText(customSearch || ""),
          sortColumnName: sort?.field || "",
          sortColumnDirection: sort?.dir || "",
          startIndex: customState.skip || 0,
          pageSize: customState.take || 10,
        });

        const formattedData = (response.data.Eops || []).map((item) => ({
          ...item,
          Check_Date: parseDate(item?.Check_Date?.split(" ")[0]),
          //Check_Status: item?.Check_Status?.toLowerCase() === "p" ? 'Paid' : 'Pending',
          Check_Status: item?.Check_Status,
          //Paid_Amount: Number(item.Paid_Amount).toFixed(2)
          Paid_Amount: parseFloat(item?.Paid_Amount || 0),
        }));

        setResults(formattedData);
        setERARetrivalMethod(response?.data?.ERARetrivalMethod);
        setTotalRecords(response?.data?.Eops[0]?.TotalRecord || 0);
        setGridLoading(false);
      } catch (err) {
        setTotalRecords(0);
        console.log("HANDLESEARCH2 ERROR : " + err);

        setResults([]);
        setGridLoading(false);
        setIsItTextSearch(false);
      } finally {
        setGridLoading(false);
      }
    } else {
      toast.error("API not configured.", { title: "Search EOP" });
    }
  };

  const handleClear = () => {
    setBillingProvider(billingProvider);
    setCheckNumber("");
    setGroupId("");
    setServiceStart("");
    setServiceEnd("");
    setResults([]);
    setSearched(false);
    setLoading(false);
    setError(null);
    setSearchText("");
    setDataState({ skip: 0, take: 10, sort: [], filter: null });
  };

  // Apply free-text search across every visible column.
  //const filteredResults = useMemo(() => {
  //    if (!searchText.trim()) return results;
  //    const q = searchText.trim().toLowerCase();
  //    return results.filter((e) => {
  //        return [
  //            //e.eopNumber,
  //            e.Check_Number, e.Name_On_Check, e.Check_Date?.toLocaleDateString('en-US', {
  //                month: '2-digit',
  //                day: '2-digit',
  //                year: 'numeric'
  //            }),
  //            e.Paid_Amount, e.Check_Status,
  //        ]
  //            .filter((v) => v !== undefined && v !== null && v !== '')
  //            .some((v) => String(v).toLowerCase().includes(q));
  //    });
  //}, [results, searchText]);

  // Reset to page 0 when the search shrinks the result set.
  useEffect(() => {
    setDataState((s) => ({ ...s, skip: 0 }));
  }, [searchText]);

  // Pre-process the filtered result set so Cards and Table share the same
  // page/sort slice.
  // const gridData = process(filteredResults, dataState);

  const gridData = {
    data: results,
    total: totalRecords,
  };

  const StatusCell = (props) => {
    const value = props.dataItem.Check_Status;

    //const badgeClass = value.toLowerCase();
    const badgeClass = value.toLowerCase();
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <span className={`status-badge ${badgeClass}`}>{value}</span>
      </td>
    );
  };

  const AmountCell = (props) => {
    const value = props.dataItem.Paid_Amount;
    const num = Number(value);

    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        {value}
      </td>
    );
  };

  //const downloadCsvFile = (response, fileName) => {

  //    //console.log("CSV DATA : "+response.data.substring(0, 500));
  //    const blob = new Blob([response.data], {
  //        type: "text/csv;charset=utf-8;"
  //    });

  //    const url = URL.createObjectURL(blob);

  //    const a = document.createElement("a");
  //    a.href = url;
  //    a.download = fileName;
  //    document.body.appendChild(a);
  //    a.click();

  //    document.body.removeChild(a);
  //    URL.revokeObjectURL(url);
  //};
  //const downloadPdfFile = (response, fileName) => {

  //    //console.log("typeof response.data : "+typeof response.data);

  //    //console.log(
  //    //    "response.data instanceof Blob : " + (response.data instanceof Blob)
  //    //);

  //    const blob = response.data;

  //    const downloadUrl = window.URL.createObjectURL(blob);

  //    const a = document.createElement("a");
  //    a.href = downloadUrl;
  //    a.download = fileName;
  //    document.body.appendChild(a);
  //    a.click();

  //    a.remove();
  //    window.URL.revokeObjectURL(downloadUrl);
  //};

  const handleDownloadPdf = async (id, billProvider, chkNumber, fileName) => {
    if (isApiConfigured) {
      try {
        //setEopPdfDownloadRequestingId(props.id);
        setEopPdfDownloadRequestingId(id);

        //let billProvider = props.dataItem.ReqbillingProvider;
        //let chkNumber = props.dataItem.Check_Number;
        //let fileName = props.dataItem.File_Name.Pdf_File;

        //alert(billProvider + "|" + chkNumber + "|" + fileName);
        //return;

        if (!billProvider?.trim() || !chkNumber?.trim() || !fileName?.trim()) {
          toast.error("Something went wrong! try again...", {
            title: "EOP Download",
          });

          return;
        }

        const response = await eopApi.downloadEOP({
          billingProvider: billProvider,
          checkNumber: chkNumber,
          fileName: fileName,
        });

        //await downloadPdfFile(response, fileName);
        await PdfFileDownload(response, fileName);
      } catch (err) {
        setEopPdfDownloadRequestingId(null);

        toast.error("Oops! Something went wrong.... try again", {
          title: "EOP Download",
        });
      } finally {
        setEopPdfDownloadRequestingId(null);
      }
    }
  };

  const handleDownloadCsv = async (props) => {
    if (isApiConfigured) {
      try {
        setEopCsvDownloadRequestingId(props.id);

        let billProvider = props.dataItem.ReqbillingProvider;
        let chkNumber = props.dataItem.Check_Number;
        let fileName = props.dataItem.File_Name.Csv_File;

        //alert(billProvider + "|" + chkNumber + "|" + fileName);
        //return;

        if (!billProvider?.trim() || !chkNumber?.trim() || !fileName?.trim()) {
          toast.error("Something went wrong! try again...", {
            title: "EOP Download",
          });

          return;
        }

        const response = await eopApi.downloadEOP({
          billingProvider: billProvider,
          checkNumber: chkNumber,
          fileName: fileName,
        });

        //  await downloadCsvFile(response, fileName);

        const headers = [
          "Claim_Number",
          "Claim_Seq",
          "Account_No",
          "Procedure_Code",
          "Services_Provided",
          "Member_Id",
          "Member_Name",
          "Modifier",
          "Service_Date",
          "Service_Thru",
          "Svc_Count",
          "Billed_Price",
          "Plan_Price",
          "Discount_Amt",
          "Paid_Amt",
          "Copay_Amt",
          "Not_Covered_Amt",
          "Deductible_Amt",
          "Co_Ins_Amt",
          "COB_Amt",
          "Ex_Code",
          "Ex_Description",
          "Your_Rep",
          "Services_Desc",
          "TableName",
        ];
        await CsvFileDownload(response, fileName, headers);
      } catch (err) {
        setEopCsvDownloadRequestingId(null);
        toast.error("Oops! Something went wrong.... try again", {
          title: "EOP Download",
        });
      } finally {
        setEopCsvDownloadRequestingId(null);
      }
    }
  };

  const handleDetailEopRequest = async (props) => {
    //  alert(JSON.stringify(props.dataItem));
    // return;

    const hasCsv = !!props.dataItem.File_Name?.Csv_File?.trim();
    const hasPdf = !!props.dataItem.File_Name?.Pdf_File?.trim();

    const eopType =
      !hasCsv && !hasPdf ? "Both" : !hasCsv ? "CsvFile" : "CheckEOP";

    //alert(eopType);
    //return;

    if (isApiConfigured) {
      //  setLoading(true);

      try {
        setRequestingId(props.id);

        let billProvider = props.dataItem.ReqbillingProvider;
        let chkNumber = props.dataItem.Check_Number;
        let paidAmount = props.dataItem.Paid_Amount.toString();

        //alert(billProvider + "|" + chkNumber + "|" + paidAmount);
        //return;

        if (!billProvider?.trim() && chkNumber?.trim()) {
          toast.error("Billing provider and check numb&& is required", {
            title: "EOP Request",
          });
          setEopRequesting(false);
          return;
        }

        const response = await eopApi.requestEOP({
          billingProvider: billProvider,
          checkNumber: chkNumber,
          paidAmount: paidAmount,
          //eopType: "CsvFile",
          eopType: eopType,
          userId: user.userId,
          token: accessToken,
        });

        if (response.success) {
          setHiddenRequestButtonsTable((prev) => [
            ...prev,
            props.dataItem.Check_ID,
          ]);

          toast.success(response.message, { title: "EOP Request" });
        } else {
          toast.error("Oops! Something went wrong.... try again", {
            title: "EOP Request",
          });
        }
      } catch (err) {
        toast.error("Oops! Something went wrong.... try again", {
          title: "EOP Request",
        });
      } finally {
        setRequestingId(null);
      }
    }
  };

  const DownloadCell = (props) => {
    // console.log("FILE NAME :"+JSON.stringify(props.File_Name));
    //return (
    //    <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>

    //        <button
    //            className="btn-secondary"
    //            style={{ padding: '6px 14px', fontSize: 13 }}
    //            onClick={() => alert('Download initiated')}
    //        >
    //            Download
    //        </button>
    //    </td>
    //);

    //return (
    //    <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
    //        {props.dataItem.File_Name?.Csv_File != null && props.dataItem.File_Name?.Csv_File !== "" ? (
    //            <>
    //            <button
    //                className="btn-secondary m-2"
    //                style={{ padding: '6px 14px', fontSize: 13 }}
    //                onClick={() => alert('Download initiated')}
    //            >
    //                Download CSV
    //            </button>
    //            <button
    //                className="btn-secondary m-2"
    //                style={{ padding: '6px 14px', fontSize: 13 }}
    //                onClick={() => alert('Download initiated')}
    //            >
    //                Download PDF
    //                </button>
    //            </>
    //        ) : (
    //            <button
    //                className="btn-primary"
    //                style={{ padding: '6px 14px', fontSize: 13 }}
    //                onClick={() => handleDetailEopRequest(props)}
    //            >
    //                    {requestingId === props.id
    //                        ? 'Requesting...'
    //                        : 'Request'}
    //            </button>
    //        )}
    //    </td>
    //);

    const hasCsv = !!props.dataItem.File_Name?.Csv_File?.trim();
    const hasPdf = !!props.dataItem.File_Name?.Pdf_File?.trim();

    const showRequest = !hasCsv || !hasPdf;

    const requestBtnLabel =
      !hasCsv && !hasPdf ? "Request" : !hasCsv ? "Request CSV" : "Request PDF";

    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        {hasCsv && (
          <button
                    className="btn-secondary w-32 px-2 py-2 m-1"
            style={{
            //  padding: "6px 14px",              
              //margin: "2px",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #0D9488, #0F766E)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              fontFamily: "inherit",
              boxShadow: "0 2px 6px rgba(13,148,136,0.2)",
            }}

            onClick={() => handleDownloadCsv(props)}
          >
            {/*eopCsvDownloadRequestingId*/}
            {/*Download CSV*/}
            <Download size={13} strokeWidth={2.5} />
            {eopCsvDownloadRequestingId === props.id
              ? "Downloading CSV..."
              : "Download CSV"}
          </button>
        )}

        {hasPdf && (
          <button
                    className="btn-secondary w-32 px-2 py-2 m-1"
            style={{
              //padding: "6px 14px",
             // margin: "2px",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #0D9488, #0F766E)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              fontFamily: "inherit",
              boxShadow: "0 2px 6px rgba(13,148,136,0.2)",
            }}
            /*onClick={() => handleDownloadPdf(props)}*/
            onClick={() =>
              handleDownloadPdf(
                props.id,
                props.dataItem.ReqbillingProvider,
                props.dataItem.Check_Number,
                props.dataItem.File_Name.Pdf_File,
              )
            }
          >
            {/*Download PDF*/}
            <Download size={13} strokeWidth={2.5} />
            {eopPdfDownloadRequestingId === props.id
              ? "Downloading PDF..."
              : "Download PDF "}
          </button>
        )}

        {showRequest &&
          !hiddenRequestButtonsTable.includes(props.dataItem.Check_ID) && (
            <button
              name="btnRequestEopTable"
              id={`btnRequestEopTable${props.dataItem.Check_ID}`}
              className="btn-primary m-1"
              style={{
                //padding: "6px 14px",
               // fontSize: 13,
                  //margin: "2px",

                  width: 120,
                  height: 36,
                  boxSizing: "border-box",

                //marginTop: 'auto',
                //padding: '8px 14px',
                borderRadius: 10,
                border: "none",
                background: "linear-gradient(135deg, #0D9488, #0F766E)",
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                fontFamily: "inherit",
                boxShadow: "0 2px 6px rgba(13,148,136,0.2)",
              }}
              onClick={() => handleDetailEopRequest(props)}
            >
              <Upload size={13} strokeWidth={2.5} />
              {requestingId === props.id
                ? "Requesting..."
                : //    : "Request"
                  requestBtnLabel}
            </button>
          )}

         {/*835 ERA (EDI) for this check — the full remittance, all claims on it */}
        {props.dataItem.Check_Number?.toString().trim() && (
            <button
                name="btnDownload835Card"
                onClick={async () => {
                    try {
                        //   await eopApi.download835({ checkNumber: eop.Check_Number, billingProvider: eop.ReqbillingProvider });
                        toast.success(
                            "This feature is currently under development and will be available soon. Thank you for your patience.",
                            { title: "ERA Download" },
                        );
                    } catch (e) {
                        toast.error(e?.response?.data?.Message || e?.message || 'Could not download the 835.', { title: 'ERA download' });
                    }
                }}
                    title="Download the X12 835 remittance (ERA) for this check — import into your billing system"
                    className="w-32 px-2 py-2 m-1"
                    style={{
                        
                   // marginTop: 'auto',
                   // padding: '8px 14px',
                        //   padding: '6px 14px',

                        width: 120,
                        height: 36,
                        boxSizing: "border-box",
                    borderRadius: 10,
                    border: '1.5px solid #0D9488',
                    background: '#fff',
                    color: '#0F766E',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    fontFamily: 'inherit',
                }}
            >
                    <Download size={13} strokeWidth={2.5} />
                    
                Download 835 (ERA)
            </button>
        )}
      </td>
    );
  };

  /* ── Card view for EOPs ────────────────────────────────────── */
  const EOPCard = ({ eop }) => {
    //const toast = useToast();
    //const { user } = useAuth();
    //const [accessToken, setAccessToken] = useState("");

    //useEffect(() => {
    //    setAccessToken(localStorage.getItem('sendero_token'));

    //}, []);

    //console.log("EOP CARD : "+JSON.stringify(eop))
    const status = (eop.Check_Status || "").toLowerCase();
    //const status = (eop.Check_Status || '').toLowerCase() == "p" ? "Paid" : "Pending";

    //const [eopCheckId, setEopCheckId] = useState(null);
    const [eopCsvDownloadRequestingIdCard, setEopCsvDownloadRequestingIdCard] =
      useState(null);
    const [eopPdfDownloadRequestingIdCard, setEopPdfDownloadRequestingIdCard] =
      useState(null);
    const [eopRequestingIdCard, setEopRequestingIdCard] = useState(null);

    const handleDownloadPdfCard = async (
      id,
      billProvider,
      chkNumber,
      fileName,
    ) => {
      if (isApiConfigured) {
        try {
          //alert(id + "|" + billProvider + "|" + chkNumber + "|" + fileName);

          setEopPdfDownloadRequestingIdCard(id);

          //let billProvider = props.dataItem.ReqbillingProvider;
          //let chkNumber = props.dataItem.Check_Number;
          //let fileName = props.dataItem.File_Name.Pdf_File;

          //alert(billProvider + "|" + chkNumber + "|" + fileName);
          //return;

          if (
            !billProvider?.trim() ||
            !chkNumber?.trim() ||
            !fileName?.trim()
          ) {
            toast.error("Something went wrong! try again...", {
              title: "EOP Download",
            });

            return;
          }

          const response = await eopApi.downloadEOP({
            billingProvider: billProvider,
            checkNumber: chkNumber,
            fileName: fileName,
          });

          //await downloadPdfFileCard(response, fileName);

          await PdfFileDownload(response, fileName);
        } catch (err) {
          setEopPdfDownloadRequestingIdCard(null);
          toast.error("Oops! Something went wrong.... try again", {
            title: "EOP Download",
          });
        } finally {
          setEopPdfDownloadRequestingIdCard(null);
        }
      }
    };

    //const downloadPdfFileCard = (response, fileName) => {

    //    const blob = response.data;

    //    const downloadUrl = window.URL.createObjectURL(blob);

    //    const a = document.createElement("a");
    //    a.href = downloadUrl;
    //    a.download = fileName;
    //    document.body.appendChild(a);
    //    a.click();

    //    a.remove();
    //    window.URL.revokeObjectURL(downloadUrl);
    //};

    const handleDownloadCsvCard = async (
      id,
      billProvider,
      chkNumber,
      fileName,
    ) => {
      if (isApiConfigured) {
        try {
          setEopCsvDownloadRequestingIdCard(id);

          //alert("CARD : "+billProvider + "|" + chkNumber + "|" + fileName);
          //return;

          if (
            !billProvider?.trim() ||
            !chkNumber?.trim() ||
            !fileName?.trim()
          ) {
            toast.error("Something went wrong! try again...", {
              title: "EOP Download",
            });

            return;
          }

          const response = await eopApi.downloadEOP({
            billingProvider: billProvider,
            checkNumber: chkNumber,
            fileName: fileName,
          });

          // await downloadCsvFileCard(response, fileName);

          const headers = [
            "Claim_Number",
            "Claim_Seq",
            "Account_No",
            "Procedure_Code",
            "Services_Provided",
            "Member_Id",
            "Member_Name",
            "Modifier",
            "Service_Date",
            "Service_Thru",
            "Svc_Count",
            "Billed_Price",
            "Plan_Price",
            "Discount_Amt",
            "Paid_Amt",
            "Copay_Amt",
            "Not_Covered_Amt",
            "Deductible_Amt",
            "Co_Ins_Amt",
            "COB_Amt",
            "Ex_Code",
            "Ex_Description",
            "Your_Rep",
            "Services_Desc",
            "TableName",
          ];
          await CsvFileDownload(response, fileName, headers);
        } catch (err) {
          setEopCsvDownloadRequestingIdCard(null);
          toast.error("Oops! Something went wrong.... try again", {
            title: "EOP Download",
          });
        } finally {
          setEopCsvDownloadRequestingIdCard(null);
        }
      }
    };
    //const downloadCsvFileCard = (response, fileName) => {

    //    //console.log("CSV DATA : "+response.data.substring(0, 500));
    //    const blob = new Blob([response.data], {
    //        type: "text/csv;charset=utf-8;"
    //    });

    //    const url = URL.createObjectURL(blob);

    //    const a = document.createElement("a");
    //    a.href = url;
    //    a.download = fileName;
    //    document.body.appendChild(a);
    //    a.click();

    //    document.body.removeChild(a);
    //    URL.revokeObjectURL(url);
    //};

    const handleDetailEopRequestCard = async (
      id,
      billProvider,
      chkNumber,
      paidAmount,
      eopType,
    ) => {
      if (isApiConfigured) {
        try {
          setEopRequestingIdCard(id);

          //alert(id+"|"+billProvider + "|" + chkNumber + "|" + paidAmount);
          //return;

          if (!billProvider?.trim() && chkNumber?.trim()) {
            toast.error("Billing provider and check numb&& is required", {
              title: "EOP Request",
            });

            return;
          }

          //alert(eopType);
          //return;

          const response = await eopApi.requestEOP({
            billingProvider: billProvider,
            checkNumber: chkNumber,
            paidAmount: paidAmount.toString(),
            // eopType: "CsvFile",
            eopType: eopType,
            userId: user.userId,
            token: accessToken,
          });

          if (response.success) {
            setHiddenRequestButtonsTable((prev) => [...prev, id]);

            toast.success(response.message, { title: "EOP Request" });
            const element = document.getElementById(`btnRequestEopCard${id}`);
            if (element) {
              element.style.display = "none";
            }
          } else {
            toast.error("Oops! Something went wrong.... try again", {
              title: "EOP Request",
            });
          }

          //   setEopRequesting(false);
        } catch (err) {
          toast.error("Oops! Something went wrong.... try again", {
            title: "EOP Request",
          });
          setEopRequestingIdCard(null);
        } finally {
          setEopRequestingIdCard(null);
        }
      }
    };

    const hasCsv = !!eop.File_Name?.Csv_File?.trim();
    const hasPdf = !!eop.File_Name?.Pdf_File?.trim();

    const showRequest = !hasCsv || !hasPdf;

    const requestBtnLabel =
      !hasCsv && !hasPdf ? "Request" : !hasCsv ? "Request CSV" : "Request PDF";
    const eopType =
      !hasCsv && !hasPdf ? "Both" : !hasCsv ? "CsvFile" : "CheckEOP";

    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #E8ECF0",
          borderRadius: 16,
          padding: 18,
          transition: "all 0.2s",
          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "#CBD5E1";
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.06)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "#E8ECF0";
          e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.03)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        {/* Header: EOP # + status */}
        {/*<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>*/}
        {/*    <div style={{ fontSize: 14, fontWeight: 700, color: '#0D9488', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 6 }}>*/}
        {/*        <Receipt size={14} strokeWidth={2} />*/}
        {/*        {eop.eopNumber}*/}
        {/*    </div>*/}
        {/*    <span className={`status-badge ${status}`}>{eop.status}</span>*/}
        {/*</div>*/}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "#0D9488",
              fontFamily: "monospace",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Receipt size={14} strokeWidth={2} />
            {eop.Check_Number}
          </div>
          <span className={`status-badge ${status?.toLowerCase()}`}>
            {status}
          </span>
        </div>

        {/* Amount */}
        <div
          style={{
            padding: "12px 14px",
            background: "#F8FAFB",
            borderRadius: 10,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#6B7280",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            Amount
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#059669" }}>
            {/*${typeof eop.Paid_Amount === 'number' ? eop.amount.toFixed(2) : '0.00'}*/}
            ${eop.Paid_Amount}
          </div>
        </div>

        {/* Provider */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "#374151",
              lineHeight: 1.4,
            }}
          >
            <Building2 size={12} color="#6B7280" strokeWidth={2} />
            {eop.Name_On_Check}
          </div>
        </div>

        {/* Check + Payment Date */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            fontSize: 12,
            color: "#6B7280",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontWeight: 600 }}>Check:</span>
            <span style={{ color: "#1A1D21", fontFamily: "monospace" }}>
              {eop.Check_Number}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Calendar size={12} strokeWidth={2} />
            <span style={{ fontWeight: 600 }}>Paid:</span>
            <span style={{ color: "#1A1D21" }}>
              {eop?.Check_Date?.toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              })}
            </span>
          </div>
        </div>

        {hasCsv && (
          <button
            name="btnDownloadEOPCsvCardView"
            /*onClick={() => alert(`Download EOP: ${eop.eopNumber}`)}*/
            onClick={() =>
              handleDownloadCsvCard(
                eop.Check_ID,
                eop.ReqbillingProvider,
                eop.Check_Number,
                eop.File_Name.Csv_File,
              )
            }
            style={{
              marginTop: "auto",
              padding: "8px 14px",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #0D9488, #0F766E)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              fontFamily: "inherit",
              boxShadow: "0 2px 6px rgba(13,148,136,0.2)",
            }}
          >
            <Download size={13} strokeWidth={2.5} />

            {eopCsvDownloadRequestingIdCard === eop.Check_ID
              ? "Downloading CSV..."
              : "Download CSV"}
          </button>
        )}

        {hasPdf && (
          <button
            name="btnDownloadEOPPdfCardView"
            /*onClick={() => alert(`Download EOP: ${eop.eopNumber}`)}*/
            onClick={() =>
              handleDownloadPdfCard(
                eop.Check_ID,
                eop.ReqbillingProvider,
                eop.Check_Number,
                eop.File_Name.Pdf_File,
              )
            }
            style={{
              marginTop: "auto",
              padding: "8px 14px",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #0D9488, #0F766E)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              fontFamily: "inherit",
              boxShadow: "0 2px 6px rgba(13,148,136,0.2)",
            }}
          >
            <Download size={13} strokeWidth={2.5} />

            {eopPdfDownloadRequestingIdCard === eop.Check_ID
              ? "Downloading PDF..."
              : "Download PDF"}
          </button>
        )}

        {/* 835 ERA (EDI) for this check — the full remittance, all claims on it */}
            {/*{user?.userRole?.trim().toLowerCase() === "super admin" && (*/}
            {["super admin", "admin"].includes(
                user?.userRole?.trim().toLowerCase()
            ) &&(
          <>
            {eop.Check_Number?.toString().trim() && (
              <button
                name="btnDownload835Card"
                onClick={async () => {
                  try {
                    toast.success(
                      "This feature is currently under development and will be available soon. Thank you for your patience.",
                      { title: "ERA Download" },
                    );
                    // await eopApi.download835({ checkNumber: eop.Check_Number, billingProvider: eop.ReqbillingProvider });
                  } catch (e) {
                    toast.error(
                      e?.response?.data?.Message ||
                        e?.message ||
                        "Could not download the 835.",
                      { title: "ERA download" },
                    );
                  }
                }}
                title="Download the X12 835 remittance (ERA) for this check — import into your billing system"
                style={{
                  marginTop: "auto",
                  padding: "8px 14px",
                  borderRadius: 10,
                  border: "1.5px solid #0D9488",
                  background: "#fff",
                  color: "#0F766E",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  fontFamily: "inherit",
                }}
              >
                <Download size={13} strokeWidth={2.5} />
                Download 835 (ERA)
              </button>
            )}
          </>
        )}
        {user?.userRole?.trim().toLowerCase() !== "super admin" &&
          eraRetrivalMethod?.trim().toLowerCase() ===
            "download from portal" && (
            <>
              {eop.Check_Number?.toString().trim() && (
                <button
                  name="btnDownload835Card"
                  onClick={async () => {
                    try {
                      toast.success(
                        "This feature is currently under development and will be available soon. Thank you for your patience.",
                        { title: "ERA Download" },
                      );
                      // await eopApi.download835({ checkNumber: eop.Check_Number, billingProvider: eop.ReqbillingProvider });
                    } catch (e) {
                      toast.error(
                        e?.response?.data?.Message ||
                          e?.message ||
                          "Could not download the 835.",
                        { title: "ERA download" },
                      );
                    }
                  }}
                  title="Download the X12 835 remittance (ERA) for this check — import into your billing system"
                  style={{
                    marginTop: "auto",
                    padding: "8px 14px",
                    borderRadius: 10,
                    border: "1.5px solid #0D9488",
                    background: "#fff",
                    color: "#0F766E",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    fontFamily: "inherit",
                  }}
                >
                  <Download size={13} strokeWidth={2.5} />
                  Download 835 (ERA)
                </button>
              )}
            </>
          )}

        {showRequest && !hiddenRequestButtonsTable.includes(eop.Check_ID) && (
          <button
            name="btnRequestEopCard"
            id={`btnRequestEopCard${eop.Check_ID}`}
            /*onClick={() => alert(`Download EOP: ${eop.eopNumber}`)}*/
            onClick={() =>
              handleDetailEopRequestCard(
                eop.Check_ID,
                eop.ReqbillingProvider,
                eop.Check_Number,
                eop.Paid_Amount,
                eopType,
              )
            }
            style={{
              //marginTop: "auto",
              marginTop: "2px",
              padding: "8px 14px",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #0D9488, #0F766E)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              fontFamily: "inherit",
              boxShadow: "0 2px 6px rgba(13,148,136,0.2)",
            }}
          >
            <Upload size={13} strokeWidth={2.5} />

            {eopRequestingIdCard === eop.Check_ID
              ? "Requesting..."
              : //: "Request"
                requestBtnLabel}
          </button>
        )}
      </div>
    );
  };

  const CheckDateCell = (props) => {
    const value = props?.dataItem?.Check_Date?.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        {value || <span style={{ color: "#9CA3AF" }}>--</span>}
      </td>
    );
  };

  return (
    <div className="main-content">
      {pageLoading && (
        <div style={styles.overlay}>
          <LoadingSpinner
            data-cy="page-loader-profile"
            message="Please wait..."
            fullPage="true"
          />
        </div>
      )}
      <div className="page-header">
        <div>
          <h1>EOP Search</h1>
          <p>Search for Explanation of Payment records</p>
        </div>
      </div>

      <div className="card">
        <h3>Search EOP Records</h3>
        <div className="form-grid">
          {/*{user?.userRole?.trim().toLowerCase() !== "super admin" && (*/}
          {!["super admin", "admin"].includes(
            user?.userRole?.trim().toLowerCase(),
          ) && (
            <div className="form-group">
              <label className="required">Billing Provider ID</label>
              <select
                name="txtBillingProvider"
                value={billingProvider}
                onChange={(e) => setBillingProvider(e.target.value)}
              >
                <option value="">Select Billing Provider ID</option>
                {userTaxIds?.map((id, index) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/*{user?.userRole?.trim().toLowerCase() === "super admin" && (*/}
          {["super admin", "admin"].includes(
            user?.userRole?.trim().toLowerCase(),
          ) && (
            <div className="form-group">
              <label className="required">Billing Provider</label>
              <input
                name="txtBillingProvider"
                type="text"
                value={billingProvider}
                onChange={(e) => setBillingProvider(e.target.value)}
                placeholder="Enter Billing Provider ID"
              />
            </div>
          )}
          <div className="form-group">
            <label>Group ID</label>
            <select
              name="selGroupId"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
            >
              <option value="">All Groups</option>
              {groups.map((group) => (
                <option key={group.Group_Id} value={group.Group_Id}>
                  {group.Group_Id}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Check Number</label>
            <input
              name="txtCheckNumber"
              type="text"
              value={checkNumber}
              onChange={(e) => setCheckNumber(e.target.value)}
              placeholder="Enter Check Number"
            />
          </div>

          <div className="form-group">
            <label className="required">Check From</label>
            <input
              name="dateCheckFrom"
              type="date"
              value={serviceStart}
              onChange={(e) => setServiceStart(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="required">Check To</label>
            <input
              name="dateCheckTo"
              type="date"
              value={serviceEnd}
              onChange={(e) => setServiceEnd(e.target.value)}
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            name="btnSearch"
            className="btn-primary"
            // onClick={handleSearch}
            onClick={() =>
              handleSearch({
                ...dataState,
                skip: 0,
              })
            }
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? "Searching…" : "Search"}
          </button>
          <button
            name="btnClear"
            className="btn-secondary"
            onClick={handleClear}
            disabled={loading}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Loading & Error States */}
      {/*{loading && (*/}
      {/*    <div className="card">*/}
      {/*        <LoadingSpinner message="Searching EOP records..." />*/}
      {/*    </div>*/}
      {/*)}*/}

      {error && <ErrorCard message={error} onRetry={handleSearch} />}

      {searched &&
      !loading &&
      !error &&
      !isItTextSearch &&
      results.length === 0 ? (
        <div
          className="card"
          style={{ textAlign: "center", padding: "48px 24px" }}
        >
          <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>
            <Search size={36} color="#9CA3AF" strokeWidth={1.5} />
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "var(--text-primary)",
              marginBottom: 4,
            }}
          >
            No EOP Records Found
          </div>
          <div style={{ fontSize: 14, color: "var(--text-secondary)" }}>
            Try different search criteria
          </div>
        </div>
      ) : (
        <>
          {searched && !loading && !error && (
            <>
              {/* Result counter + View toggle */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                  padding: "0 2px",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                {/*<span style={{ fontSize: 14, color: '#6B7280', fontWeight: 500 }}>*/}
                {/*    Showing {filteredResults.length} of {results.length} EOP{results.length !== 1 ? 's' : ''}*/}
                {/*</span>*/}

                <span
                  style={{ fontSize: 14, color: "#6B7280", fontWeight: 500 }}
                >
                  {/*Showing {dataState.skip + 1} - {Math.min(dataState.skip + results.length, totalRecords)} of {totalRecords} EOP{totalRecords !== 1 ? 's' : ''}*/}
                  {totalRecords === 0
                    ? "Showing 0 of 0 EOPs"
                    : `Showing ${dataState.skip + 1} - ${Math.min(
                        dataState.skip + results.length,
                        totalRecords,
                      )} of ${totalRecords} EOP${totalRecords !== 1 ? "s" : ""}`}
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  {/* Free-text search — works in both Cards and Table views */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      background: "#fff",
                      border: "1px solid #E8ECF0",
                      borderRadius: 10,
                      minWidth: 220,
                    }}
                  >
                    <Search size={14} color="#94A3B8" />
                    <input
                      name="txtSearch"
                      type="text"
                      value={searchText}
                      //  onChange={(e) => setSearchText(e.target.value)}
                      onChange={(e) => {
                        const value = e.target.value;

                        setSearchText(value);

                        const newState = {
                          ...dataState,
                          skip: 0,
                        };

                        setDataState(newState);

                        // Clear previous timer
                        if (searchTimeoutRef.current) {
                          clearTimeout(searchTimeoutRef.current);
                        }

                        // Set new timer (3 seconds)
                        searchTimeoutRef.current = setTimeout(() => {
                          handleSearch2(newState, value);
                        }, 3000);
                      }}
                      placeholder="Search results…"
                      style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        fontSize: 13,
                        color: "#1A1D21",
                        background: "transparent",
                        fontFamily: "inherit",
                        minWidth: 0,
                      }}
                    />
                    {searchText && (
                      <button
                        name="btnClearSearch"
                        /*onClick={() => setSearchText('')}*/
                        onClick={() => {
                          const newState = {
                            ...dataState,
                            skip: 0,
                          };

                          setSearchText("");
                          setDataState(newState);

                          handleSearch2(newState, "");
                        }}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          padding: 0,
                          color: "#94A3B8",
                          display: "inline-flex",
                        }}
                        title="Clear search"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      background: "#F1F5F9",
                      borderRadius: 10,
                      padding: 3,
                      gap: 2,
                    }}
                  >
                    <button
                      onClick={() => setViewMode("cards")}
                      style={toggleBtnStyle(viewMode === "cards")}
                      title="Card view"
                      aria-pressed={viewMode === "cards"}
                    >
                      <LayoutGrid size={14} strokeWidth={2.5} /> Cards
                    </button>
                    <button
                      onClick={() => setViewMode("table")}
                      style={toggleBtnStyle(viewMode === "table")}
                      title="Table view"
                      aria-pressed={viewMode === "table"}
                    >
                      <List size={14} strokeWidth={2.5} /> Table
                    </button>
                  </div>
                  <Button
                    themeColor="primary"
                    fillMode="outline"

                    onClick={async () => {
                      if (excelExportRef.current) {
                        {
                          /*        // Export the filtered (post-search) result set so the*/
                        }
                        {
                          /*        // export matches what the user sees on screen.*/
                        }

                        //const exportData = filteredResults.map(item => ({
                        //    ...item

                        //}));

                        //excelExportRef.current.save(exportData);

                        const newState = {
                          ...dataState,
                          skip: 0,
                        };

                        const exportData = await exportToExcel(newState, "");

                        excelExportRef.current.save(exportData);
                      }
                    }}
                    style={{ fontSize: 13 }}
                  >
                    {IsExportingToExcel
                      ? "Exporting to Excel..."
                      : "Export to Excel"}
                  </Button>
                </div>
              </div>

              {/* ExcelExport available in both Cards + Table views */}
              <ExcelExport ref={excelExportRef} fileName="EOPSearch_Export">
                {/*<ExcelExportColumn field="eopNumber" title="EOP Number" width={180} />*/}
                <ExcelExportColumn
                  field="Check_Number"
                  title="Check Number"
                  width={150}
                />
                <ExcelExportColumn
                  field="Name_On_Check"
                  title="Provider"
                  width={220}
                />
                <ExcelExportColumn
                  field="Check_Date"
                  title="Payment Date"
                  width={140}
                />
                <ExcelExportColumn
                  field="Paid_Amount"
                  title="Amount"
                  width={120}
                />
                <ExcelExportColumn
                  field="Check_Status"
                  title="Status"
                  width={120}
                />
              </ExcelExport>

              <div style={{ position: "relative" }}>
                {gridLoading && (
                  <div style={styles.gridOverlay}>
                    <LoadingSpinner
                      data-cy="grid-loader"
                      message="Loading Eops..."
                    />
                  </div>
                )}
                {viewMode === "cards" ? (
                  //filteredResults.length === 0 ? (
                  totalRecords === 0 ? (
                    <div
                      className="card"
                      style={{ textAlign: "center", padding: "48px 24px" }}
                    >
                      <div style={{ fontSize: 14, color: "#6B7280" }}>
                        No EOP records match your search.
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(280px, 1fr))",
                          gap: 16,
                        }}
                      >
                        {/* gridData.data is the post-search/sort/page slice */}
                        {gridData.data.map((e) => (
                          <EOPCard key={e.Check_ID} eop={e} />
                        ))}
                      </div>
                      <CardsPagination
                        total={gridData.total}
                        skip={dataState.skip}
                        take={dataState.take}
                        // onPageChange={({ skip, take }) => setDataState((s) => ({ ...s, skip, take }))}
                        onPageChange={({ skip, take }) => {
                          const newState = {
                            ...dataState,
                            skip,
                            take,
                          };

                          setDataState(newState);
                          handleSearch2(newState, searchText);
                        }}
                        pageSizes={[5, 10, 20]}
                      />
                    </>
                  )
                ) : (
                  <div
                    className="card"
                    style={{ padding: 0, overflow: "hidden" }}
                  >
                    <div className="grid-scroll-wrapper">
                      <Grid
                        //data={gridData}
                        scrollable="scrollable"
                        sortable={true}
                        resizable={true}
                        reorderable={true}
                        pageable={{ pageSizes: [5, 10, 20] }}
                        filterable={false}
                        data={gridData.data}
                        total={gridData.total}
                        skip={dataState.skip}
                        take={dataState.take}
                        sort={dataState.sort}
                        sortable={true}
                        //  onDataStateChange={(e) => setDataState(e.dataState)}
                        //{...dataState}
                        onDataStateChange={(e) => {
                          setDataState(e.dataState);
                          handleSearch2(e.dataState, searchText);
                        }}
                        loading={gridLoading}
                        style={{ border: "none" }}
                      >
                        {/*<GridColumn field="eopNumber" title="EOP Number" width="180px" columnMenu={ColumnMenu} />*/}
                        <GridColumn
                          field="Check_Number"
                          title="Check Number"
                          width="150px"
                          //columnMenu={ColumnMenu}
                          columnMenu={ColumnMenuClaim}
                        />
                        <GridColumn
                          field="Name_On_Check"
                          title="Provider"
                          width="220px"
                          //columnMenu={ColumnMenu}
                          columnMenu={ColumnMenuClaim}
                        />
                        <GridColumn
                          field="Check_Date"
                          title="Payment Date"
                          width="140px"
                          // columnMenu={ColumnMenu}
                          columnMenu={ColumnMenuClaim}
                          cells={{ data: CheckDateCell }}
                          format="{0:MM/dd/yyyy}"
                          filter="date"
                        />
                        <GridColumn
                          field="Paid_Amount"
                          title="Amount ($)"
                          width="130px"
                          // columnMenu={ColumnMenu}
                          columnMenu={ColumnMenuClaim}
                          /*cells={{ data: AmountCell }}*/
                        />
                        <GridColumn
                          field="Check_Status"
                          title="Status"
                          width="140px"
                          //columnMenu={ColumnMenu}
                          columnMenu={ColumnMenuClaim}
                          cells={{ data: StatusCell }}
                        />
                        <GridColumn
                          title="Actions"
                          width="175px"
                          cells={{ data: DownloadCell }}
                        />
                      </Grid>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

const toggleBtnStyle = (active) => ({
  padding: "7px 12px",
  borderRadius: 8,
  border: "none",
  background: active ? "#fff" : "transparent",
  color: active ? "#0D9488" : "#6B7280",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
  fontFamily: "inherit",
  transition: "all 0.15s",
});

export default EOPSearchPage;
